import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  Avatar,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  ChevronRight,
  EventNote,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { appointmentService } from '../services/appointmentService';
import { useApp } from '../context/AppContext';
import AppointmentStatusBadge from './AppointmentStatusBadge';

const UpcomingAppointmentsWidget: React.FC = () => {
  const { state } = useApp();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await appointmentService.getMyAppointments();
      
      // Filter for upcoming appointments only. Fresh bookings are REQUESTED until
      // the caregiver accepts, so patients must see those here too.
      const now = new Date();
      const upcomingStatuses = ['REQUESTED', 'SCHEDULED', 'CONFIRMED'];
      const upcoming = (data || [])
        .filter(apt => {
          const scheduledDate = new Date(apt.scheduledAt);
          return scheduledDate >= now && upcomingStatuses.includes(apt.status);
        })
        .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
        .slice(0, 3); // Show only next 3 appointments
      
      setAppointments(upcoming);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const isToday = date.toDateString() === today.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    
    if (isToday) {
      return `Today at ${timeStr}`;
    } else if (isTomorrow) {
      return `Tomorrow at ${timeStr}`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }
  };

  const getTimeUntil = (dateString: string) => {
    const appointmentDate = new Date(dateString);
    const now = new Date();
    const diffMs = appointmentDate.getTime() - now.getTime();
    
    if (diffMs < 0) return null;
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 24) {
      if (diffHours < 1) return 'Soon';
      if (diffHours === 1) return '1h';
      return `${diffHours}h`;
    }
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return '1d';
    if (diffDays < 7) return `${diffDays}d`;
    
    const diffWeeks = Math.floor(diffDays / 7);
    return `${diffWeeks}w`;
  };

  const isPatient = state.user?.role === 'PATIENT';

  if (loading) {
    return (
      <Card>
        <CardHeader 
          title="Upcoming Appointments"
          avatar={<CalendarToday />}
        />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader 
        title="Upcoming Appointments"
        avatar={<CalendarToday color="primary" />}
        action={
          <Button 
            size="small" 
            endIcon={<ChevronRight />}
            onClick={() => navigate('/appointments')}
          >
            View All
          </Button>
        }
      />
      <Divider />
      <CardContent sx={{ p: 0 }}>
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {appointments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
            <CalendarToday sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              No upcoming appointments
            </Typography>
            {isPatient && (
              <Button 
                size="small" 
                variant="outlined" 
                sx={{ mt: 2 }}
                onClick={() => navigate('/search')}
              >
                Find Caregivers
              </Button>
            )}
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {appointments.map((appointment, index) => {
              const otherParty = isPatient ? appointment.caretaker : appointment.patient;
              const timeUntil = getTimeUntil(appointment.scheduledAt);
              
              return (
                <React.Fragment key={appointment.id}>
                  <ListItem
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      py: 2,
                    }}
                    onClick={() => navigate('/appointments')}
                    secondaryAction={
                      timeUntil && (
                        <Chip 
                          label={timeUntil} 
                          size="small" 
                          color="primary"
                          sx={{ fontWeight: 'bold' }}
                        />
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={otherParty?.avatar}>
                        {otherParty?.firstName?.[0]}{otherParty?.lastName?.[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle2">
                            {otherParty?.firstName} {otherParty?.lastName}
                          </Typography>
                          <AppointmentStatusBadge status={appointment.status} size="small" />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {appointment.service?.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <AccessTime sx={{ fontSize: 14 }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(appointment.scheduledAt)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < appointments.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
            
            {appointments.length > 0 && (
              <>
                <Divider />
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Button 
                    fullWidth 
                    variant="text"
                    endIcon={<ChevronRight />}
                    onClick={() => navigate('/appointments')}
                  >
                    View All Appointments
                  </Button>
                </Box>
              </>
            )}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingAppointmentsWidget;


